import React, { useEffect, useState, useRef } from 'react';
import { Button } from '@mui/material';
import {
  academicYears as AY_OPTIONS,
  defaultAcademicYear
} from '../../../constants/academicYears';
import { ButtonBox, FieldBox, FiltersContainer } from '../styled';
import DropdownSelect from '../../../components/DropdownSelect';
import { SnackbarSeverityType } from '../../../enums';
import electivesAssignmentApi from '../../../apis/electivesAssignment';
import { parseStudents } from '../utils/utils';
import userUtils from '../../../utils/userUtils';
import { getSchoolClassesConfig } from '../../../apis/schoolConfig';
import { SchoolClassesConfig, Class } from '../../../types/schoolConfigType';
import { SearchFiltersProps } from '../types/ElectiveAssignmentTypes';
import Icons from '../../../components/icons';

const SearchFilters: React.FC<SearchFiltersProps> = ({
  school,
  user,
  onResults,
  showToast,
  onLoadingChange,
  allowYearChange = false,
  onFiltersChange
}) => {
  const [academicYear, setAcademicYear] = useState(defaultAcademicYear);
  const [grade, setGrade] = useState('');
  const [division, setDivision] = useState('');
  const [gradeOptions, setGradeOptions] = useState<string[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<string[]>([]);
  const [classesConfig, setClassesConfig] = useState<Class[]>([]);
  const [loading, setLoading] = useState(false);
  const fetchedConfigRef = useRef(false);

  const disableSearch = !grade || !division || loading;

  useEffect(() => {
    if (!school || fetchedConfigRef.current) return;
    fetchedConfigRef.current = true;
    getSchoolClassesConfig(school)
      .then((response) => {
        const config = response.data as SchoolClassesConfig;
        setGradeOptions(config.grades || []);
        setClassesConfig(config.classes || []);
      })
      .catch(() =>
        showToast('Failed to load grades.', SnackbarSeverityType.error)
      );
    // eslint-disable-next-line
  }, [school]);

  useEffect(() => {
    fetchedConfigRef.current = false;
  }, [school]);

  useEffect(() => {
    setDivisionOptions(
      classesConfig
        .filter((classes) => classes.grade === grade)
        .map((classes) => classes.division)
        .filter(Boolean) as string[]
    );
  }, [grade, classesConfig]);

  useEffect(() => {
    const course = userUtils.getCurrentCourseInfo(user);
    if (course?.grade && gradeOptions.includes(course.grade) && !grade) {
      setGrade(course.grade);
    }
    if (
      course?.division &&
      divisionOptions.includes(course.division) &&
      !division
    ) {
      setDivision(course.division);
    }
  }, [user, gradeOptions, divisionOptions, grade, division]);

  const handleAcademicYearChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: unknown; name: string } })
  ) => {
    const target = e.target as HTMLInputElement & { value: string };
    setAcademicYear(target.value);
    onFiltersChange?.();
  };
  const handleGradeChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: unknown; name: string } })
  ) => {
    const target = e.target as HTMLInputElement & { value: string };
    setGrade(target.value);
    setDivision('');
    onFiltersChange?.();
  };
  const handleDivisionChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: unknown; name: string } })
  ) => {
    const target = e.target as HTMLInputElement & { value: string };
    setDivision(target.value);
    onFiltersChange?.();
  };

  const handleSearch = async () => {
    if (!school || !academicYear || !grade || !division) return;
    try {
      setLoading(true);
      onLoadingChange?.(true);
      const response =
        await electivesAssignmentApi.getStudentElectiveSubjectsMapping(
          school,
          academicYear,
          grade,
          division
        );
      const studentsData = parseStudents(response);
      onResults(studentsData, {
        academicYear,
        grade,
        division,
        termIds: response.termIds
      });
    } catch (e) {
      showToast(
        'Failed to fetch student electives.',
        SnackbarSeverityType.error
      );
      onResults([], { academicYear, grade, division, termIds: [] });
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  };

  return (
    <FiltersContainer>
      <FieldBox>
        <DropdownSelect
          label='Academic Year'
          name='academicYear'
          value={academicYear}
          onChange={handleAcademicYearChange}
          options={AY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          size='small'
          disabled={!allowYearChange}
          placeholder='Academic Year'
        />
      </FieldBox>

      <FieldBox>
        <DropdownSelect
          label='Grade'
          name='grade'
          value={grade}
          onChange={handleGradeChange}
          options={gradeOptions.map((g) => ({ value: g, label: g }))}
          size='small'
          placeholder='Grade'
        />
      </FieldBox>

      <FieldBox>
        <DropdownSelect
          label='Division'
          name='division'
          value={division}
          onChange={handleDivisionChange}
          options={divisionOptions.map((d) => ({ value: d, label: d }))}
          size='small'
          disabled={!grade}
          placeholder='Division'
        />
      </FieldBox>
      <ButtonBox>
        <Button
          variant='contained'
          onClick={handleSearch}
          disabled={disableSearch}
        >
          <Icons.SearchWhiteOutlined style={{ marginRight: '6px' }} />
          Search
        </Button>
      </ButtonBox>
    </FiltersContainer>
  );
};

export default SearchFilters;
